import { Route } from '@angular/router';
import { AuthGuard } from './core/auth/guards/auth.guard';
import { ConsentsGuard } from './core/auth/guards/consents.guard';
import { EmailVerificationGuard } from './core/auth/guards/emailVerification.guard';
import { NoAuthGuard } from './core/auth/guards/noAuth.guard';
import { LayoutComponent } from './commons/layout/layout.component';

export const appRoutes: Route[] = [
  { path: '', pathMatch: 'full', redirectTo: 'home' },

  // Auth routes for guests
  {
    path: '',
    canActivate: [NoAuthGuard],
    canActivateChild: [NoAuthGuard],
    children: [
      {
        path: 'sign-in',
        loadComponent: () =>
          import('./features/auth/pages/sign-in/sign-in.component').then(
            (m) => m.SignInComponent,
          ),
      },
      {
        path: 'sign-up',
        loadComponent: () =>
          import('./features/auth/pages/sign-up/sign-up.component').then(
            (m) => m.SignUpComponent,
          ),
      },
      {
        path: 'forgot-password',
        loadComponent: () =>
          import(
            './features/auth/pages/forgot-password/forgot-password.component'
          ).then((m) => m.ForgotPasswordComponent),
      },
      {
        path: 'reset-password',
        loadComponent: () =>
          import(
            './features/auth/pages/reset-password/reset-password.component'
          ).then((m) => m.ResetPasswordComponent),
      },
    ],
  },

  // Email verification routes for authenticated users
  {
    path: '',
    children: [
      {
        path: 'confirmation-required',
        loadComponent: () =>
          import(
            './features/auth/pages/confirmation-required/confirmation-required.component'
          ).then((m) => m.ConfirmationRequiredComponent),
      },
      {
        path: 'consents-required',
        loadComponent: () =>
          import(
            './features/auth/pages/consents-required/consents-required.component'
          ).then((m) => m.ConsentsRequiredComponent),
      },
    ],
  },

  // Basic routes for guests
  {
    path: '',
    component: LayoutComponent,
    canActivate: [ConsentsGuard],
    canActivateChild: [ConsentsGuard],
    children: [
      {
        path: 'home',
        pathMatch: 'full',
        loadComponent: () =>
          import('./features/hero/pages/hero/hero.component').then(
            (m) => m.HeroComponent,
          ),
      },
      {
        path: 'search',
        pathMatch: 'full',
        loadComponent: () =>
          import('./features/search/pages/search/search.component').then(
            (m) => m.SearchComponent,
          ),
      },
      {
        path: 'campsites/:campsiteSlug',
        children: [
          {
            path: 'accommodations/:accommodationSlug',
            children: [
              {
                path: '',
                pathMatch: 'full',
                loadComponent: () =>
                  import(
                    './features/accommodation/pages/accommodation/accommodation.component'
                  ).then((m) => m.AccommodationComponent),
              },
            ],
          },
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import(
                './features/campsite/pages/campsite/campsite.component'
              ).then((m) => m.CampsiteComponent),
          },
        ],
      },
      {
        path: 'checkout',
        pathMatch: 'full',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        loadComponent: () =>
          import(
            './features/reservation/pages/reservation-checkout/reservation-checkout.component'
          ).then((m) => m.ReservationCheckoutComponent),
      },
    ],
  },

  // Support routes
  {
    path: '',
    component: LayoutComponent,
    children: [
      //  ATTENZIONE: Non modificare i nomi delle rotte in questo blocco.
      //  Le rotte sono utilizzate anche dall'applicazione mobile e qualsiasi
      //  modifica potrebbe causare malfunzionamenti nell'app mobile.

      {
        path: 'terms-and-conditions',
        loadComponent: () =>
          import(
            './features/support/pages/terms-and-conditions/terms-and-conditions.component'
          ).then((m) => m.TermsAndConditionsComponent),
      },
      {
        path: 'privacy',
        loadComponent: () =>
          import(
            './features/support/pages/privacy-and-cookies/privacy-and-cookies.component'
          ).then((m) => m.PrivacyAndCookiesComponent),
      },
      {
        path: 'help',
        loadComponent: () =>
          import(
            './features/support/pages/help-center/help-center.component'
          ).then((m) => m.HelpCenterComponent),
      },

      // Utility routes
      {
        path: 'join-us',
        pathMatch: 'full',
        loadComponent: () =>
          import('./features/support/pages/join-us/join-us.component').then(
            (m) => m.JoinUsComponent,
          ),
      },
      {
        path: 'download-app',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './features/support/pages/download-app/download-app.component'
          ).then((m) => m.DownloadAppComponent),
      },
      {
        path: 'maintenance',
        loadComponent: () =>
          import('./features/maintenance/maintenance.component').then(
            (m) => m.MaintenanceComponent,
          ),
      },
    ],
  },

  // Reservations routes for authenticated users
  {
    path: 'reservations',
    component: LayoutComponent,
    canActivate: [AuthGuard, EmailVerificationGuard, ConsentsGuard],
    canActivateChild: [AuthGuard, EmailVerificationGuard, ConsentsGuard],
    children: [
      {
        path: 'history',
        loadComponent: () =>
          import(
            './features/reservation/pages/past-reservations/past-reservations.component'
          ).then((m) => m.PastReservationsComponent),
      },
      {
        path: ':bookingId',
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import(
                './features/reservation/pages/reservation/reservation.component'
              ).then((m) => m.ReservationComponent),
          },
        ],
      },
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './features/reservation/pages/reservations/reservations.component'
          ).then((m) => m.ReservationsComponent),
      },
    ],
  },

  // Stripe callbacks
  {
    path: 'stripe-cb',
    canActivate: [AuthGuard, EmailVerificationGuard, ConsentsGuard],
    canActivateChild: [AuthGuard, EmailVerificationGuard, ConsentsGuard],
    children: [
      {
        path: 'setup/success',
        loadComponent: () =>
          import(
            './features/payment/pages/payment-setup-succeeded/payment-setup-succeeded.component'
          ).then((m) => m.PaymentSetupSucceededComponent),
      },
      {
        // Mostro nuovamente la pagina di checkout in caso di cancellazione
        path: 'setup/cancel',
        loadComponent: () =>
          import(
            './features/payment/pages/payment-setup-cancelled/payment-setup-cancelled.component'
          ).then((m) => m.PaymentSetupCancelledComponent),
      },
      {
        path: 'checkout/success',
        loadComponent: () =>
          import(
            './features/payment/pages/payment-succeeded/payment-succeeded.component'
          ).then((m) => m.PaymentSucceededComponent),
      },
      {
        // Mostro nuovamente la pagina di checkout in caso di cancellazione

        path: 'checkout/cancel',
        loadComponent: () =>
          import(
            './features/payment/pages/payment-setup-cancelled/payment-setup-cancelled.component'
          ).then((m) => m.PaymentSetupCancelledComponent),
      },
    ],
  },

  {
    path: '',
    component: LayoutComponent,
    canActivate: [AuthGuard, EmailVerificationGuard, ConsentsGuard],
    canActivateChild: [AuthGuard, EmailVerificationGuard, ConsentsGuard],
    children: [
      // User profile routes for authenticated users
      {
        path: 'account',
        children: [
          {
            path: 'personal-info',
            loadComponent: () =>
              import(
                './features/user/components/account/account.component'
              ).then((m) => m.AccountComponent),
          },
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import(
                './features/user/components/account/account.component'
              ).then((m) => m.AccountComponent),
          },
        ],
      },
    ],
  },

  { path: '**', redirectTo: '/' },
];
